import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db, getCollectionName } from './firebase';
import { doc, onSnapshot, collection, getDoc } from './firestore-compat';

// =============================
// HELPERS
// =============================
const formatDateBR = (timestamp) => {
  if (!timestamp) return '—';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const TIPO_CONFIG = {
  hospitalizacao: { label: 'Hospitalização', color: '#f97316', bg: '#fff7ed', icon: '🟠' },
  implantacao: { label: 'Implantação', color: '#3b82f6', bg: '#eff6ff', icon: '🔵' },
};

const STATUS_CONFIG = {
  aberto: { label: 'Em Aberto', color: '#f97316', bg: '#fff7ed' },
  em_andamento: { label: 'Em Andamento', color: '#3b82f6', bg: '#eff6ff' },
  concluido: { label: 'Concluído', color: '#10b981', bg: '#f0fdf4' },
};

const SETOR_STATUS_CONFIG = {
  pendente: { label: 'Pendente', color: '#6b7280', bg: '#f9fafb' },
  em_andamento: { label: 'Em Andamento', color: '#3b82f6', bg: '#eff6ff' },
  concluido: { label: 'Concluído', color: '#10b981', bg: '#f0fdf4' },
};

// =============================
// ESTILOS
// =============================
const S = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0f4ff 0%, #fafbff 100%)',
    padding: '24px',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  },
  container: { maxWidth: '1000px', margin: '0 auto' },
  breadcrumb: {
    display: 'flex', alignItems: 'center', gap: '8px',
    marginBottom: '20px', fontSize: '0.88rem', color: '#6b7280',
  },
  breadcrumbLink: {
    color: '#667eea', textDecoration: 'none', fontWeight: '600',
    display: 'flex', alignItems: 'center', gap: '4px',
  },
  card: {
    background: '#fff', borderRadius: '16px', padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e8ecf4',
    marginBottom: '20px',
  },
  headerCard: (tipo) => ({
    background: '#fff', borderRadius: '16px', padding: '28px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #e8ecf4',
    borderLeft: `5px solid ${TIPO_CONFIG[tipo]?.color || '#667eea'}`,
    marginBottom: '20px',
  }),
  tagTipo: (tipo) => ({
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '4px 12px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '700',
    background: TIPO_CONFIG[tipo]?.bg || '#f0f4ff',
    color: TIPO_CONFIG[tipo]?.color || '#667eea',
    border: `1px solid ${TIPO_CONFIG[tipo]?.color || '#667eea'}33`,
    marginBottom: '12px',
  }),
  pacienteNome: {
    fontSize: '1.8rem', fontWeight: '800', color: '#1a1a2e', margin: '0 0 8px 0',
  },
  numIW: {
    fontSize: '0.88rem', color: '#6b7280', margin: '0 0 16px 0',
    display: 'flex', alignItems: 'center', gap: '6px',
  },
  statusBadge: (status) => ({
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    padding: '5px 14px', borderRadius: '20px', fontSize: '0.82rem', fontWeight: '700',
    background: STATUS_CONFIG[status]?.bg || '#f9fafb',
    color: STATUS_CONFIG[status]?.color || '#6b7280',
    border: `1px solid ${STATUS_CONFIG[status]?.color || '#6b7280'}33`,
  }),
  metaRow: {
    display: 'flex', flexWrap: 'wrap', gap: '16px',
    marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f4ff',
    fontSize: '0.82rem', color: '#6b7280',
  },
  metaItem: { display: 'flex', alignItems: 'center', gap: '5px' },
  progressContainer: {
    marginTop: '16px',
  },
  progressLabel: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '0.78rem', color: '#6b7280', marginBottom: '6px',
  },
  progressBar: {
    height: '10px', borderRadius: '6px', background: '#e8ecf4', overflow: 'hidden',
  },
  progressFill: (pct) => ({
    height: '100%', width: `${pct}%`,
    background: 'linear-gradient(90deg, #667eea, #764ba2)',
    borderRadius: '6px', transition: 'width 0.4s ease',
  }),
  vinculoCard: (tipo) => ({
    background: TIPO_CONFIG[tipo]?.bg || '#f0f4ff',
    border: `1px solid ${TIPO_CONFIG[tipo]?.color || '#667eea'}44`,
    borderRadius: '14px', padding: '20px', marginBottom: '20px',
  }),
  vinculoTitle: {
    fontSize: '0.78rem', fontWeight: '700', color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px',
  },
  sectionTitle: {
    fontSize: '1rem', fontWeight: '700', color: '#2d3748',
    margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px',
  },
  setoresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  setorCard: (status) => ({
    background: '#fff', borderRadius: '12px', padding: '18px',
    border: '1px solid #e8ecf4',
    borderLeft: `4px solid ${SETOR_STATUS_CONFIG[status || 'pendente']?.color || '#6b7280'}`,
    boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
  }),
  setorNome: {
    fontSize: '0.92rem', fontWeight: '700', color: '#1a1a2e', margin: '0 0 8px 0',
  },
  setorStatusBadge: (status) => ({
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    padding: '3px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '700',
    background: SETOR_STATUS_CONFIG[status || 'pendente']?.bg,
    color: SETOR_STATUS_CONFIG[status || 'pendente']?.color,
    marginBottom: '10px',
  }),
  setorTratativa: {
    fontSize: '0.82rem', color: '#4b5563', lineHeight: '1.5',
    margin: '0 0 8px 0', fontStyle: 'italic',
  },
  setorMeta: {
    fontSize: '0.75rem', color: '#9ca3af',
    display: 'flex', flexDirection: 'column', gap: '2px',
  },
  anexoLink: {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    color: '#667eea', fontSize: '0.78rem', textDecoration: 'none',
    marginTop: '6px', fontWeight: '600',
  },
  loadingBox: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '200px', fontSize: '0.95rem', color: '#6b7280',
  },
  errorBox: {
    textAlign: 'center', padding: '60px 24px', color: '#6b7280',
  },
};

// =============================
// COMPONENTE PRINCIPAL
// =============================
function OcorrenciaDetalhe() {
  const { id } = useParams();
  const [ocorrencia, setOcorrencia] = useState(null);
  const [setores, setSetores] = useState([]);
  const [vinculada, setVinculada] = useState(null);
  const [loading, setLoading] = useState(true);

  // Carregar ocorrência principal
  useEffect(() => {
    if (!id) return;
    const unsub = onSnapshot(doc(db, getCollectionName('ocorrencias_v2'), id), (snap) => {
      if (snap.exists()) {
        setOcorrencia({ id: snap.id, ...snap.data() });
      } else {
        setOcorrencia(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, [id]);

  // Carregar subcoleção de setores
  useEffect(() => {
    if (!id) return;
    const unsub = onSnapshot(collection(db, getCollectionName('ocorrencias_v2'), id, 'tarefas'), (snap) => {
      setSetores(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [id]);

  // Carregar ocorrência vinculada
  useEffect(() => {
    if (!ocorrencia?.vinculoId) { setVinculada(null); return; }
    const fetchVinculada = async () => {
      try {
        const snap = await getDoc(doc(db, getCollectionName('ocorrencias_v2'), ocorrencia.vinculoId));
        if (snap.exists()) setVinculada({ id: snap.id, ...snap.data() });
      } catch (e) {
        console.warn('Erro ao carregar ocorrência vinculada:', e);
      }
    };
    fetchVinculada();
  }, [ocorrencia?.vinculoId]);

  // Calcular progresso
  const progresso = setores.length > 0
    ? Math.round((setores.filter(s => s.status === 'concluido').length / setores.length) * 100)
    : 0;

  // =============================
  // RENDER
  // =============================
  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.container}>
          <div style={S.loadingBox}>⏳ Carregando ocorrência...</div>
        </div>
      </div>
    );
  }

  if (!ocorrencia) {
    return (
      <div style={S.page}>
        <div style={S.container}>
          <div style={S.errorBox}>
            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🔍</div>
            <h2>Ocorrência não encontrada</h2>
            <p>O registro solicitado não existe ou foi removido.</p>
            <Link to="/gestao-ocorrencias" style={S.breadcrumbLink}>
              ← Voltar à Gestão de Ocorrências
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const tipo = ocorrencia.tipo || 'hospitalizacao';
  const status = ocorrencia.status || 'aberto';
  const tipoConfig = TIPO_CONFIG[tipo] || TIPO_CONFIG.hospitalizacao;

  return (
    <div style={S.page}>
      <div style={S.container}>

        {/* BREADCRUMB */}
        <div style={S.breadcrumb}>
          <Link to="/gestao-ocorrencias" style={S.breadcrumbLink}>
            ← Gestão de Ocorrências
          </Link>
          <span>/</span>
          <span>Detalhe da Ocorrência</span>
        </div>

        {/* HEADER CARD */}
        <div style={S.headerCard(tipo)}>
          <div style={S.tagTipo(tipo)}>
            {tipoConfig.icon} {tipoConfig.label}
          </div>
          <h1 style={S.pacienteNome}>{ocorrencia.paciente?.nomeCompleto || ocorrencia.nomePaciente || 'Paciente sem nome'}</h1>
          <p style={S.numIW}>
            <span>🔖</span>
            <strong>Nº IW:</strong> {ocorrencia.paciente?.numIW || ocorrencia.numeroIW || '—'}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={S.statusBadge(status)}>
              {STATUS_CONFIG[status]?.label || status}
            </span>
            {ocorrencia.vinculoId && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '5px 12px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '600',
                background: '#ede9fe', color: '#7c3aed', border: '1px solid #7c3aed33',
              }}>
                🔗 Vinculada
              </span>
            )}
          </div>

          <div style={S.progressContainer}>
            <div style={S.progressLabel}>
              <span>Progresso dos Setores</span>
              <span style={{ fontWeight: '700', color: '#667eea' }}>{progresso}%</span>
            </div>
            <div style={S.progressBar}>
              <div style={S.progressFill(progresso)} />
            </div>
          </div>

          <div style={S.metaRow}>
            <span style={S.metaItem}>
              📅 <strong>Abertura:</strong> {formatDateBR(ocorrencia.dataAbertura)}
            </span>
            {ocorrencia.dataConclusao && (
              <span style={S.metaItem}>
                ✅ <strong>Conclusão:</strong> {formatDateBR(ocorrencia.dataConclusao)}
              </span>
            )}
            <span style={S.metaItem}>
              👤 <strong>Criado por:</strong> {ocorrencia.criadoPorNome || '—'}
            </span>
            {ocorrencia.observacoes && (
              <span style={{ ...S.metaItem, flexBasis: '100%' }}>
                📝 {ocorrencia.observacoes}
              </span>
            )}
          </div>
        </div>

        {/* OCORRÊNCIA VINCULADA */}
        {vinculada && (
          <div style={S.vinculoCard(vinculada.tipo)}>
            <div style={S.vinculoTitle}>
              🔗 Ocorrência Vinculada — Histórico do Paciente
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={S.tagTipo(vinculada.tipo)}>
                {TIPO_CONFIG[vinculada.tipo]?.icon} {TIPO_CONFIG[vinculada.tipo]?.label}
              </span>
              <strong style={{ fontSize: '1rem', color: '#1a1a2e' }}>{vinculada.paciente?.nomeCompleto || vinculada.nomePaciente}</strong>
              <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>Nº IW: {vinculada.paciente?.numIW || vinculada.numeroIW}</span>
              <span style={S.statusBadge(vinculada.status)}>
                {STATUS_CONFIG[vinculada.status]?.label || vinculada.status}
              </span>
              <Link
                to={`/ocorrencias/${vinculada.id}`}
                style={{ ...S.breadcrumbLink, marginLeft: 'auto' }}
              >
                Ver detalhe →
              </Link>
            </div>
          </div>
        )}

        {/* SETORES */}
        <div style={S.card}>
          <h2 style={S.sectionTitle}>
            📋 Tarefas / Áreas
            <span style={{ fontSize: '0.78rem', color: '#b0b8c9', fontWeight: '400' }}>
              {setores.filter(s => s.status === 'concluido').length}/{setores.length} concluídas
            </span>
          </h2>

          {setores.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#b0b8c9', padding: '40px 0', fontStyle: 'italic' }}>
              Nenhum setor cadastrado para esta ocorrência.
            </div>
          ) : (
            <div style={S.setoresGrid}>
              {setores.map(setor => (
                <div key={setor.id} style={S.setorCard(setor.status)}>
                  <h3 style={S.setorNome}>{setor.area || setor.setor || 'Área sem nome'}</h3>
                  <div style={S.setorStatusBadge(setor.status)}>
                    {SETOR_STATUS_CONFIG[setor.status || 'pendente']?.label}
                  </div>
                  <p style={S.setorTratativa}>
                    {setor.tratativa || 'Sem tratativa registrada.'}
                  </p>
                  <div style={S.setorMeta}>
                    {setor.responsavelNome && (
                      <span>👤 {setor.responsavelNome}</span>
                    )}
                    {setor.dataRegistro && (
                      <span>📅 {formatDateBR(setor.dataRegistro)}</span>
                    )}
                    {setor.dataConclusao && setor.status === 'concluido' && (
                      <span>✅ Concluído em {formatDateBR(setor.dataConclusao)}</span>
                    )}
                  </div>
                  {setor.anexoUrl && (
                    <a
                      href={setor.anexoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={S.anexoLink}
                    >
                      📎 {setor.anexoNome || 'Ver Anexo'}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default OcorrenciaDetalhe;
